import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { FaArrowLeft } from 'react-icons/fa6';
import { HiOutlineInformationCircle } from 'react-icons/hi2';
import { TbLink } from 'react-icons/tb';
import { toast } from 'react-toastify';
import { RootState } from '../../../redux/reducers';
import Community, { CommunityRole } from '../../../types/community';
import { AnySubscriptionType, SubscriptionType } from '../../../types/misc';
import businessService from '../../../services/business/businessService';
import communityService from '../../../services/community/communityService';
import ModalLayout from '../layouts/ModalLayout';
import ModalHeader from '../misc/ModalHeader';
import SearchBar from '../forms/SearchBar';
import CancelSaveButton from '../misc/CancelSaveButton';
import ProfilePicture from '../misc/ProfilePicture';
import { SubscriptionSelector } from '../selection-lists/SubscriptionSelector';

export interface ConnectExistingCommunityModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  businessId: number;
  mainCommunityId?: number;
  linkedCommunityIds: number[];
  onCommunityConnected: () => void;
}

type ModalStep = 'select' | 'subscription';

export default function ConnectExistingCommunityModal({
  open,
  setOpen,
  businessId,
  mainCommunityId,
  linkedCommunityIds,
  onCommunityConnected,
}: ConnectExistingCommunityModalProps): JSX.Element {
  const { t } = useTranslation();
  const communities = useSelector((state: RootState) => state.application.communities);
  const businesses = useSelector((state: RootState) => state.application.businesses);

  const [step, setStep] = useState<ModalStep>('select');
  const [search, setSearch] = useState('');
  const [selectedCommunityId, setSelectedCommunityId] = useState<number | null>(null);
  const [subscriptionType, setSubscriptionType] = useState<SubscriptionType>(SubscriptionType.FREE);
  const [loading, setLoading] = useState(false);

  const mainCommunityIds = useMemo(
    () =>
      new Set(
        (businesses ?? [])
          .map((b) => b.community?.id)
          .filter((id): id is number => id != null),
      ),
    [businesses],
  );

  const linkedIds = useMemo(() => new Set(linkedCommunityIds), [linkedCommunityIds]);

  const connectableCommunities = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (communities ?? [])
      .filter(
        (community) =>
          community.role === CommunityRole.ADMIN &&
          community.dashboard &&
          !mainCommunityIds.has(community.id) &&
          !linkedIds.has(community.id) &&
          community.id !== mainCommunityId,
      )
      .filter(
        (community) =>
          !query ||
          community.name.toLowerCase().includes(query) ||
          community.type.toLowerCase().includes(query),
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [communities, linkedIds, mainCommunityId, mainCommunityIds, search]);

  const selectedCommunity = connectableCommunities.find((c) => c.id === selectedCommunityId);

  useEffect(() => {
    if (!open) return;
    communityService.fetchCommunity();
    setStep('select');
    setSearch('');
    setSelectedCommunityId(null);
    setSubscriptionType(SubscriptionType.FREE);
  }, [open]);

  const handleClose = () => {
    if (loading) return;
    setOpen(false);
  };

  const handleConnect = async () => {
    if (!selectedCommunityId || !subscriptionType) return;

    setLoading(true);
    try {
      await businessService.connectExistingCommunity(businessId, selectedCommunityId, subscriptionType);
      toast.success(t('page.communities.connectModal.success'));
      setOpen(false);
      onCommunityConnected();
    } catch (error) {
      console.error('Failed to connect community:', error);
      toast.error(t('page.communities.connectModal.error'));
    } finally {
      setLoading(false);
    }
  };

  if (step === 'select') {
    return (
      <ModalLayout
        open={open}
        setOpen={handleClose}
        closeButton={!loading}
        className="z-50 mx-6 flex w-fit min-w-[450px] max-w-[650px] flex-col gap-[20px] rounded-[12px] bg-secondary-50 px-10 py-6 pb-4 pt-5">
        <ModalHeader
          icon={TbLink}
          title={t('page.communities.connectModal.title')}
          subtitle={t('page.communities.connectModal.subtitle')}
        />

        <div className="w-full border-b border-primary-300" />

        <h2 className="text-[22px] leading-[24px]">
          {t('page.communities.connectModal.selectCommunity')}
        </h2>

        <SearchBar search={search} setSearch={setSearch} />

        <CommunitySelectList
          communities={connectableCommunities}
          selectedCommunityId={selectedCommunityId}
          onSelect={setSelectedCommunityId}
          emptyMessage={t('page.communities.connectModal.empty')}
        />

        <div className="w-full border-b border-primary-300" />

        <div className="flex flex-col items-end gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p>
            {selectedCommunityId ? 1 : 0} {t('page.communities.connectModal.selected')}
          </p>
          <CancelSaveButton
            buttonText={t('general.continue')}
            onCancel={handleClose}
            onSave={() => setStep('subscription')}
            disabled={!selectedCommunityId || loading}
          />
        </div>
      </ModalLayout>
    );
  }

  return (
    <ModalLayout
      open={open}
      setOpen={handleClose}
      closeButton={!loading}
      className="relative z-50 mx-6 transform overflow-hidden rounded-[12px] bg-secondary-50 px-6 py-6 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-6xl">
      <button
        type="button"
        onClick={() => setStep('select')}
        disabled={loading}
        className="mb-4 flex items-center gap-2 font-semibold text-primary-900 transition-opacity hover:opacity-70 disabled:opacity-40">
        <FaArrowLeft className="h-4 w-4" />
        {t('general.back')}
      </button>

      <ModalHeader
        icon={TbLink}
        title={t('page.communities.connectModal.subscriptionTitle')}
        subtitle={selectedCommunity?.name}
      />

      <div className="mt-6 w-full border-b border-primary-300" />

      <div className="mt-6 rounded-xl bg-primary-100 p-4">
        <div className="flex items-start gap-3">
          <HiOutlineInformationCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-900" />
          <div>
            <p className="text-sm font-semibold text-primary-900">
              {t('page.onboarding.subscription.subCommunityFreeTitle')}
            </p>
            <p className="mt-1 text-sm text-primary-900 text-opacity-70">
              {t('page.onboarding.subscription.subCommunityFreeNote')}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <SubscriptionSelector
          selectedSubscription={subscriptionType as AnySubscriptionType}
          mode="selectable"
          onSubscriptionSelect={(type) => setSubscriptionType(type as SubscriptionType)}
          showTrialPeriod={false}
          isBusiness={false}
          isSubCommunity
        />
      </div>

      <div className="mt-6 w-full border-b border-primary-300" />

      <div className="mt-6 flex justify-end">
        <CancelSaveButton
          cancelText={t('general.cancel')}
          buttonText={t('page.communities.connectModal.connect')}
          onCancel={handleClose}
          onSave={handleConnect}
          disabled={!subscriptionType || loading}
          loading={loading}
        />
      </div>
    </ModalLayout>
  );
}

interface CommunitySelectListProps {
  communities: Community[];
  selectedCommunityId: number | null;
  onSelect: (communityId: number) => void;
  emptyMessage: string;
}

function CommunitySelectList({
  communities,
  selectedCommunityId,
  onSelect,
  emptyMessage,
}: CommunitySelectListProps): JSX.Element {
  return (
    <div className="flex w-full flex-col gap-2 border-y border-primary-300 py-4">
      <div className="no-scrollbar flex max-h-[400px] flex-col gap-2 overflow-y-scroll">
        {communities.length === 0 ? (
          <p className="py-4 pl-2 text-sm text-gray-500">{emptyMessage}</p>
        ) : (
          communities.map((community) => (
            <div
              key={community.id}
              onClick={() => onSelect(community.id)}
              className={classNames(
                'flex cursor-pointer items-center justify-between rounded-[8px] border border-secondary-200 px-3 py-2 transition-all',
                {
                  'bg-secondary-200': selectedCommunityId === community.id,
                },
              )}>
              <div className="flex min-w-0 items-center gap-2">
                <ProfilePicture
                  alias={community.name}
                  picture={community.picture ?? undefined}
                  className="h-9 w-9"
                />
                <div className="min-w-0">
                  <p className="truncate font-medium">{community.name}</p>
                  <p className="truncate text-[13px] text-gray-500">{community.type}</p>
                </div>
              </div>
              <input
                type="radio"
                name="connect-community"
                checked={selectedCommunityId === community.id}
                readOnly
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
}
